import {
  Column,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  TableState,
  useReactTable,
} from '@tanstack/react-table';
import { cn } from '@c2fo/liquidity';
import emptystatements from '@/enterprise/assets/emptystatementsgrey.svg?url';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/enterprise/components/Table';
import NoDataState from '../NoDataState';
import {
  getCommonPinningStyles,
  leftShadowClassNames,
  rightShadowClassNames,
  usePinnedColumnShadow,
} from './tablePinning';

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  initialState?: Partial<TableState>;
  state?: Partial<TableState>;
  getRowProps?: (row: TData, index: number) => Record<string, unknown>;
}

const isFirstNonStickyColumn = <TData, TValue>(
  column: Column<TData, TValue>,
  headerGroup: { headers: { column: Column<TData, TValue>; id: string }[] }
): boolean => {
  const columnIndex = headerGroup.headers.findIndex((header) => header.column.id === column.id);

  if (columnIndex <= 0) {
    return false;
  }

  // check if previous column is sticky and current is not
  const prevColumn = headerGroup.headers[columnIndex - 1]?.column;
  return prevColumn?.getIsPinned() === 'left' && !column.getIsPinned();
};

const DataTable = <TData, TValue>({
  columns,
  data,
  initialState = {},
  state = {},
  getRowProps,
}: DataTableProps<TData, TValue>) => {
  const { tableRef, showLeftPinnedShadow, showRightPinnedShadow } = usePinnedColumnShadow();
  const table = useReactTable({
    columns,
    data,
    enableSortingRemoval: false,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState,
    state,
  });

  return (
    <div ref={tableRef} style={{ overflowX: 'auto', position: 'relative' }} className="relative">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead
                    key={header.id}
                    className={cn({
                      'pl-6': isFirstNonStickyColumn(header.column, headerGroup),
                      'bg-canvas': header.column.getIsPinned() === 'left',
                      [leftShadowClassNames]: header.column.getIsLastColumn('left') && showLeftPinnedShadow,
                      [rightShadowClassNames]: header.column.getIsFirstColumn('right') && showRightPinnedShadow,
                      'cursor-pointer transition-all duration-200 hover:bg-gray-100/50': header.column.getCanSort(),
                      'bg-gray-100 hover:bg-gray-100': header.column.getIsSorted(),
                    })}
                    onClick={header.column.getCanSort() ? () => header.column.toggleSorting() : undefined}
                    style={getCommonPinningStyles(header.column)}
                  >
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row, index) => {
              const rowProps = getRowProps ? getRowProps(row.original, index) : {};

              return (
                <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'} {...rowProps}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={cn({
                        'pl-6': isFirstNonStickyColumn(cell.column, {
                          headers: row.getVisibleCells().map((c) => ({ column: c.column, id: c.id })),
                        }),
                        'bg-white': cell.column.getIsPinned() === 'left',
                        [leftShadowClassNames]: cell.column.getIsLastColumn('left') && showLeftPinnedShadow,
                        [rightShadowClassNames]: cell.column.getIsFirstColumn('right') && showRightPinnedShadow,
                      })}
                      style={getCommonPinningStyles(cell.column)}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length}>
                <NoDataState
                  className="py-8"
                  icon={<img src={emptystatements} alt="emptystatements" className="h-28 w-28" />}
                  title="No Data to Display"
                />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default DataTable;
